import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
    AccountsColumnToggle,
    AccountsDensity,
    AccountsPlacement,
} from '@/pages/Console/Accounts/Shared/AccountsToolbar';
import { usePersistedAccountsViewConfig } from '@/pages/Console/Accounts/Shared/usePersistedAccountsViewConfig';

/**
 * EAA-1839: this tab's OWN table key. It namespaces both persisted display
 * preferences (`portal:accountsView:app-detail-accounts`, via
 * `usePersistedAccountsViewConfig`) and the kit's persisted column widths
 * (`ui:colwidths:app-detail-accounts`, via `AccountsTable`'s `resize` prop).
 *
 * DISTINCT FROM `realm-accounts` AND `scoped-accounts`, and that is a hard
 * requirement rather than tidiness: the three boards have different column
 * sets, so a shared key would let each overwrite the others' widths and land
 * stored widths on columns that do not exist here.
 */
export const APP_DETAIL_ACCOUNTS_TABLE_KEY = 'app-detail-accounts';

/**
 * Every column of this tab's table that may be hidden from the "Hiển thị"
 * popover. The identity column is deliberately absent — it is the only column
 * that names WHO a row is about, and it is one of the two columns left without a
 * declared width (see `AppDetailAccountsTab`'s `COLUMN_WIDTHS` note), so hiding
 * it would both strand the reader and hand its share of the leftover width to
 * the role column alone.
 *
 * `outcome` IS hideable, unlike the identity column: this tab is very often
 * read with a single outcome filter applied, and the column then repeats one
 * translated label down every row — the same observation the outcome column's
 * own comment records about its old raw-enum second line.
 */
const HIDEABLE_COLUMNS = ['organization', 'role', 'outcome', 'outcome_at', 'sync_attempts'] as const;
export type AppDetailAccountColumnKey = (typeof HIDEABLE_COLUMNS)[number];

/** Reuses each column's OWN heading as its toggle label — one string, one
 *  meaning, so the popover and the header can never disagree. */
const COLUMN_LABEL_KEYS: Record<AppDetailAccountColumnKey, string> = {
    organization: 'console.appDetail.accounts.columnOrgScope',
    role: 'console.appDetail.accounts.columnRole',
    outcome: 'console.appDetail.accounts.columnOutcome',
    outcome_at: 'console.appDetail.accounts.columnTimestamp',
    sync_attempts: 'console.appDetail.accounts.columnRetryCount',
};

/**
 * EAA-1839: the PRESENTATION state of the App Detail → "Tài khoản" tab — which
 * columns are visible and how tall a row is — split out of
 * `AppDetailAccountsTab` for the same reason `useScopedAccountViews` is split
 * out of `useScopedAccounts`: none of it reaches a request, and the tab file is
 * already long.
 *
 * PERSISTENCE IS REUSED, NOT RECOPIED. `usePersistedAccountsViewConfig` (in
 * `Accounts/`) already parameterises the storage key and already implements the
 * four rules — `try/catch` every access, versioned payload discarded rather
 * than migrated, stored keys filtered against the live column set, read once on
 * mount / write on change. Its own docstring asks callers not to make a third
 * copy, so this hook passes its table key to that one instead. If that file is
 * later promoted into `Accounts/Shared/`, this import moves with it; a copy here
 * is what would need deleting instead.
 *
 * PRECEDENCE, stated because it is easy to get backwards: a STORED value always
 * wins over the seeded default. That is expressed by ORDER — the seed is the
 * `useState` initial value and the reconciliation effect only ever overwrites it
 * once `loadState` resolves to a real config. `reconciled` gates the write
 * effect so the first render's seed cannot overwrite a just-loaded value.
 *
 * FACET-RAIL PLACEMENT (2026-08-24, user request): this tab offers the same
 * "VỊ TRÍ BỘ LỌC" section the two accounts boards do, but its DEFAULT is
 * `'header'` — the collapsed "Bộ lọc (N)" popover — where the boards default to
 * `'sidebar'`. That is not an oversight to be tidied up later: a 236px rail
 * column is a third of this tab's table width, and the tab's two width-less
 * columns (identity and role) live on the leftover, so the sidebar is the
 * EXPENSIVE choice here rather than the natural one. It is offered because the
 * user asked for it and because an operator who filters by organization
 * repeatedly wants the list standing open; `AppDetailAccountsTab` narrows its
 * four fixed columns while the rail is showing so the choice does not produce
 * ~100px identity/role columns (see `SIDEBAR_COLUMN_WIDTHS` there).
 *
 * DELIBERATELY NOT PERSISTED: the search term and the three dropdown filters.
 * Those are per-visit CONTEXT — restoring a stale `outcome=error` filter would
 * make this app look like it has far fewer accounts than it has, which is
 * exactly the honest-gap-vs-bug confusion this tab exists to avoid.
 */
export function useAppDetailAccountsView() {
    const { t } = useTranslation();
    const [density, setDensityState] = useState<AccountsDensity>('comfortable');
    const [placement, setPlacementState] = useState<AccountsPlacement>('header');
    const [hiddenColumns, setHiddenColumns] = useState<Set<AppDetailAccountColumnKey>>(
        () => new Set<AppDetailAccountColumnKey>()
    );

    const { loadState, persist } = usePersistedAccountsViewConfig(
        APP_DETAIL_ACCOUNTS_TABLE_KEY,
        HIDEABLE_COLUMNS
    );
    const [reconciled, setReconciled] = useState(false);

    useEffect(() => {
        if (loadState === 'pending') return;
        if (loadState !== 'empty') {
            setHiddenColumns(new Set(loadState.hiddenColumns as AppDetailAccountColumnKey[]));
            setDensityState(loadState.density);
            /* Optional on the stored payload — a payload written before this tab
               offered the choice carries none, and the `'header'` seed above has
               to survive that rather than being overwritten with `undefined`. */
            if (loadState.placement) setPlacementState(loadState.placement);
        }
        setReconciled(true);
    }, [loadState]);

    useEffect(() => {
        if (!reconciled) return;
        persist({ hiddenColumns: [...hiddenColumns], density, placement });
    }, [reconciled, hiddenColumns, density, placement, persist]);

    const setDensity = useCallback((next: AccountsDensity) => setDensityState(next), []);
    const setPlacement = useCallback((next: AccountsPlacement) => setPlacementState(next), []);

    const columnItems = useMemo<AccountsColumnToggle[]>(
        () =>
            HIDEABLE_COLUMNS.map((key) => ({
                key,
                label: t(COLUMN_LABEL_KEYS[key]),
                visible: !hiddenColumns.has(key),
            })),
        [t, hiddenColumns]
    );

    const toggleColumn = useCallback((key: string) => {
        setHiddenColumns((prev) => {
            const next = new Set(prev);
            const typed = key as AppDetailAccountColumnKey;
            if (next.has(typed)) next.delete(typed);
            else next.add(typed);
            return next;
        });
    }, []);

    const isColumnVisible = useCallback(
        (key: string) => !hiddenColumns.has(key as AppDetailAccountColumnKey),
        [hiddenColumns]
    );

    return {
        density,
        setDensity,
        placement,
        setPlacement,
        columnItems,
        toggleColumn,
        isColumnVisible,
    };
}

export type AppDetailAccountsViewVM = ReturnType<typeof useAppDetailAccountsView>;
